import { apiClient } from '@/lib/api/client'
import { exportToCSV } from '@/lib/utils/csvUtils'
import type { StoreFormData } from '@/lib/validations/store'
import type { StoreAssetFormData } from '@/lib/validations/asset'

export interface SelectOption {
  id: number
  name: string
  label?: string
  children?: SelectOption[]
}

export interface StoreRecord {
  id: number
  business_unit_id: number
  business_unit_name?: string
  branch_code: string
  branch_name: string
  branch_address?: string
  region_id?: number | null
  province_id?: number | null
  location_address?: string | null
  normalized_address?: string | null
  latitude?: number | null
  longitude?: number | null
  branch_email?: string | null
  contact_number?: string | null
  mobile_number?: string | null
  contact_person?: string | null
  active?: string | boolean
}

export interface StoreAssetRecord {
  /** Assignment id (DataTable primary key); on legacy details list this may be asset id */
  id: number
  asset_id?: number
  brand_name?: string | null
  category_name?: string | null
  tag_number?: string | null
  model?: string | null
  serial_number?: string | null
  function?: string | null
  manufacturer?: string | null
  sw_provider?: string | null
  warranty_date?: string | null
  order_received_date?: string | null
  ram?: string | null
  disk_space?: string | null
  os_version?: string | null
  ms_office?: string | null
  processor?: string | null
  cpu?: string | null
  ip_address?: string | null
  mac_address?: string | null
  machine_state?: string | null
  sub_state?: string | null
  remarks?: string | null
  hd_remarks?: string | null
  status?: string | null
  audit_status?: 'audited' | 'not_audited'
  deleted_at?: string | null
}

export interface StoreDetailsResponse {
  store: StoreRecord
  assets_list: StoreAssetRecord[]
  assets_summary: Record<string, { count: number; audited_count: number; pending_count: number; active_count: number }>
}

function unwrapList<T>(payload: unknown): T[] {
  if (Array.isArray(payload)) return payload as T[]
  if (payload && typeof payload === 'object' && 'data' in payload) {
    const data = (payload as { data: unknown }).data
    if (Array.isArray(data)) return data as T[]
  }
  return []
}

function flattenBusinessUnits(items: SelectOption[], depth = 0): Array<{ id: number; label: string }> {
  const result: Array<{ id: number; label: string }> = []
  for (const item of items) {
    const prefix = depth > 0 ? `${'— '.repeat(depth)}` : ''
    result.push({ id: item.id, label: `${prefix}${item.name}` })
    if (item.children?.length) {
      result.push(...flattenBusinessUnits(item.children, depth + 1))
    }
  }
  return result
}

export async function fetchBusinessUnitOptions(clientCode?: string): Promise<Array<{ id: number; label: string }>> {
  const headers = clientCode ? { 'X-Client-Code': clientCode } : undefined
  const { data } = await apiClient.get('/options/business', { headers })
  const items = unwrapList<SelectOption>(data)
  return flattenBusinessUnits(items)
}

export async function fetchRegionOptions(clientCode?: string): Promise<Array<{ id: number; name: string }>> {
  const headers = clientCode ? { 'X-Client-Code': clientCode } : undefined
  const { data } = await apiClient.get('/options/regions', { headers })
  return unwrapList<{ id: number; name: string }>(data)
}

export async function fetchProvinceOptions(regionId: number, clientCode?: string): Promise<Array<{ id: number; name: string }>> {
  const headers = clientCode ? { 'X-Client-Code': clientCode } : undefined
  const { data } = await apiClient.get(`/options/provinces/${regionId}`, { headers })
  return unwrapList<{ id: number; name: string }>(data)
}

export async function fetchStore(id: number): Promise<StoreRecord> {
  const { data } = await apiClient.get(`/store-management/stores/${id}`)
  return (data?.data ?? data) as StoreRecord
}

export async function fetchStoreDetails(id: number): Promise<StoreDetailsResponse> {
  const { data } = await apiClient.get(`/store-management/stores/${id}/details`)
  return (data?.data ?? data) as StoreDetailsResponse
}

export async function createStore(payload: StoreFormData, clientCode?: string) {
  const headers = clientCode ? { 'X-Client-Code': clientCode } : undefined
  const { data } = await apiClient.post('/store-management/stores', normalizeStorePayload(payload), { headers })
  return data
}

export async function updateStore(id: number, payload: StoreFormData, clientCode?: string) {
  const headers = clientCode ? { 'X-Client-Code': clientCode } : undefined
  const { data } = await apiClient.put(`/store-management/stores/${id}`, normalizeStorePayload(payload), { headers })
  return data
}

export async function createStoreAsset(storeId: number, payload: Record<string, unknown>) {
  const { data } = await apiClient.post(`/store-management/stores/${storeId}/assets`, payload)
  return data?.data ?? data
}

export async function updateStoreAsset(storeId: number, assetId: number, payload: Record<string, unknown>) {
  const { data } = await apiClient.put(`/store-management/stores/${storeId}/assets/${assetId}`, payload)
  return data?.data ?? data
}

export async function deleteStoreAsset(storeId: number, assignmentId: number) {
  const { data } = await apiClient.delete(`/store-management/stores/${storeId}/assets/${assignmentId}`)
  return data
}

export async function restoreStoreAsset(storeId: number, assignmentId: number) {
  const { data } = await apiClient.patch(`/store-management/stores/${storeId}/assets/${assignmentId}/restore`)
  return data
}

export async function forceDeleteStoreAsset(storeId: number, assignmentId: number) {
  const { data } = await apiClient.delete(`/store-management/stores/${storeId}/assets/${assignmentId}/force`)
  return data
}

export async function importStoreAssetAssignments(storeId: number, file: File) {
  const form = new FormData()
  form.append('file', file)
  const { data } = await apiClient.post(`/store-management/stores/${storeId}/assets/import`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return data?.data ?? data
}

function formatAssetDateInput(value: string | null | undefined): string {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 10)
  return date.toISOString().slice(0, 10)
}

export function mapStoreAssetToFormData(asset: StoreAssetRecord): StoreAssetFormData {
  return {
    brand_name: asset.brand_name ?? '',
    category_name: asset.category_name ?? '',
    model: asset.model ?? '',
    tag_number: asset.tag_number ?? '',
    serial_number: asset.serial_number ?? '',
    function: asset.function ?? '',
    manufacturer: asset.manufacturer ?? '',
    sw_provider: asset.sw_provider ?? '',
    warranty_date: formatAssetDateInput(asset.warranty_date),
    order_received_date: formatAssetDateInput(asset.order_received_date),
    ram: asset.ram ?? '',
    disk_space: asset.disk_space ?? '',
    os_version: asset.os_version ?? '',
    ms_office: asset.ms_office ?? '',
    processor: asset.processor ?? '',
    cpu: asset.cpu ?? '',
    ip_address: asset.ip_address ?? '',
    mac_address: asset.mac_address ?? '',
  }
}

export function mapAssetFormToPayload(payload: StoreAssetFormData): Record<string, unknown> {
  const tag = payload.tag_number?.trim()
  return {
    brand_name: payload.brand_name.trim(),
    category_name: payload.category_name.trim(),
    model: payload.model.trim(),
    serial_number: payload.serial_number.trim(),
    function: payload.function.trim(),
    tag_number: tag ? tag.toUpperCase() : '',
    manufacturer: payload.manufacturer?.trim() || '',
    sw_provider: payload.sw_provider?.trim() || '',
    warranty_date: payload.warranty_date?.trim() || '',
    order_received_date: payload.order_received_date?.trim() || '',
    ram: payload.ram?.trim() || '',
    disk_space: payload.disk_space?.trim() || '',
    os_version: payload.os_version?.trim() || '',
    ms_office: payload.ms_office?.trim() || '',
    processor: payload.processor?.trim() || '',
    cpu: payload.cpu?.trim() || '',
    ip_address: payload.ip_address?.trim() || '',
    mac_address: payload.mac_address?.trim() || '',
  }
}

export interface BrandOption {
  id: number
  name: string
  categories?: string | Array<{ id: number; name: string; label?: string }>
}

export interface NamedOption {
  id: number | string
  name: string
  parent_slug?: string
}

export function parseBrandMachineTypes(
  categories: BrandOption['categories']
): Array<{ id: number; name: string }> {
  if (!categories) return []
  const normalize = (items: unknown[]): Array<{ id: number; name: string }> =>
    items
      .map((item) => {
        const row = item as { id?: number; name?: string; label?: string }
        const name = String(row.name ?? row.label ?? '').trim()
        if (!name || row.id == null) return null
        return { id: Number(row.id), name }
      })
      .filter((item): item is { id: number; name: string } => item !== null)

  if (Array.isArray(categories)) {
    return normalize(categories)
  }
  if (typeof categories === 'string') {
    try {
      const parsed = JSON.parse(categories) as unknown
      return Array.isArray(parsed) ? normalize(parsed) : []
    } catch {
      return []
    }
  }
  return []
}

const SPECIFIC_CATEGORY_PARENT_SLUGS = {
  'SW Provider': 'sw-provider',
  'Disk Space': 'disk-space',
  'MS Office': 'ms-office',
  RAM: 'ram',
} as const

export type SpecificCategoryGroup = keyof typeof SPECIFIC_CATEGORY_PARENT_SLUGS

export function filterSpecificCategoryOptions(
  items: NamedOption[],
  group: SpecificCategoryGroup
): NamedOption[] {
  const parentSlug = SPECIFIC_CATEGORY_PARENT_SLUGS[group]
  return items
    .filter((item) => item.parent_slug === parentSlug)
    .sort((a, b) => a.name.localeCompare(b.name))
}

export function withCurrentOption(options: NamedOption[], currentValue: string): NamedOption[] {
  const list = [...options]
  const current = currentValue.trim()
  if (current && !list.some((item) => item.name === current)) {
    list.unshift({ id: `current-${current}`, name: current })
  }
  return list.sort((a, b) => a.name.localeCompare(b.name))
}

export async function fetchBrandOptions(): Promise<BrandOption[]> {
  const { data } = await apiClient.get('/options/brands')
  return unwrapList<BrandOption>(data)
}

export async function fetchSpecificCategoryOptions(): Promise<NamedOption[]> {
  const { data } = await apiClient.get('/options/specific-categories')
  return unwrapList<NamedOption>(data)
}

export async function fetchBrandModelOptions(
  brandId: number,
  categoryId: number
): Promise<NamedOption[]> {
  const { data } = await apiClient.get('/options/brand-models', {
    params: { brand_id: brandId, category_id: categoryId },
  })
  return unwrapList<NamedOption>(data)
}

export async function fetchFunctionOptions(categoryId: number): Promise<Array<{ name: string }>> {
  const { data } = await apiClient.get(`/options/functions/${categoryId}`)
  const list = unwrapList<{ name?: string }>(data)
  return list.filter((item) => item.name).map((item) => ({ name: String(item.name) }))
}

export async function fetchCpuOptions(): Promise<NamedOption[]> {
  const { data } = await apiClient.get('/options/cpu')
  return unwrapList<NamedOption>(data)
}

export async function fetchOsVersionOptions(): Promise<NamedOption[]> {
  const { data } = await apiClient.get('/options/os-version')
  return unwrapList<NamedOption>(data)
}

export async function fetchProcessorOptions(): Promise<NamedOption[]> {
  const { data } = await apiClient.get('/options/processor')
  return unwrapList<NamedOption>(data)
}

export interface StoreAssetOptionLists {
  specificCategories: NamedOption[]
  cpuOptions: NamedOption[]
  osVersionOptions: NamedOption[]
  processorOptions: NamedOption[]
}

const emptyStoreAssetOptionLists = (): StoreAssetOptionLists => ({
  specificCategories: [],
  cpuOptions: [],
  osVersionOptions: [],
  processorOptions: [],
})

/** Load asset form dropdowns in a single API round-trip. */
export async function fetchStoreAssetOptionLists(): Promise<StoreAssetOptionLists> {
  try {
    const { data } = await apiClient.get('/options/asset-form-lists')
    const payload = (data?.data ?? data) as Record<string, unknown>
    return {
      specificCategories: unwrapList<NamedOption>(payload.specific_categories),
      cpuOptions: unwrapList<NamedOption>(payload.cpu),
      osVersionOptions: unwrapList<NamedOption>(payload.os_version),
      processorOptions: unwrapList<NamedOption>(payload.processor),
    }
  } catch {
    const [specific, cpu, os, processor] = await Promise.allSettled([
      fetchSpecificCategoryOptions(),
      fetchCpuOptions(),
      fetchOsVersionOptions(),
      fetchProcessorOptions(),
    ])

    return {
      specificCategories: specific.status === 'fulfilled' ? specific.value : [],
      cpuOptions: cpu.status === 'fulfilled' ? cpu.value : [],
      osVersionOptions: os.status === 'fulfilled' ? os.value : [],
      processorOptions: processor.status === 'fulfilled' ? processor.value : [],
    }
  }
}

export { emptyStoreAssetOptionLists }

function normalizeStorePayload(payload: StoreFormData) {
  return {
    business_unit_id: payload.business_unit_id,
    branch_code: payload.branch_code.trim(),
    branch_name: payload.branch_name.trim(),
    branch_address: payload.branch_address?.trim() || null,
    region_id: payload.region_id || null,
    province_id: payload.province_id || null,
    location_address: payload.location_address?.trim() || null,
    normalized_address: payload.normalized_address?.trim() || null,
    latitude: payload.latitude ?? null,
    longitude: payload.longitude ?? null,
    branch_email: payload.branch_email?.trim() || null,
    contact_number: payload.contact_number?.trim() || null,
    mobile_number: payload.mobile_number?.trim() || null,
    contact_person: payload.contact_person?.trim() || null,
    active: payload.active,
  }
}

export async function importStores(file: File) {
  const form = new FormData()
  form.append('file', file)
  const { data } = await apiClient.post('/store-management/stores/import', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return data?.data ?? data
}

export function downloadStoresTemplate() {
  const link = document.createElement('a')
  link.href = '/templates/stores_template.csv'
  link.download = 'stores_template.csv'
  link.click()
}

/** CSV columns aligned with import template (`public/templates/stores_template.csv`). */
export const STORE_IMPORT_CSV_HEADERS = [
  'branch_name',
  'business_unit',
  'branch_code',
  'branch_address',
  'region',
  'province',
  'branch_email',
  'contact_person',
  'contact_number',
  'mobile_number',
  'location_address',
  'normalized_address',
  'latitude',
  'longitude',
  'active',
] as const

function parseEmbeddedJson<T extends Record<string, unknown>>(value: unknown): T | null {
  if (!value) return null
  if (typeof value === 'object') return value as T
  if (typeof value === 'string') {
    try {
      return JSON.parse(value) as T
    } catch {
      return null
    }
  }
  return null
}

function formatStoreActiveForCsv(active: unknown): string {
  if (active === 'Active' || active === true || active === 1 || active === '1') return '1'
  return '0'
}

function formatCoordinate(value: unknown): string {
  if (value === null || value === undefined || value === '') return ''
  const num = Number(value)
  return Number.isFinite(num) ? String(num) : ''
}

/** Map API store record to import-compatible CSV row (round-trip for batch upload). */
export function mapStoreToImportCsvRow(store: Record<string, unknown>): string[] {
  const region = parseEmbeddedJson<{ name?: string }>(store.region)
  const province = parseEmbeddedJson<{ province_name?: string }>(store.province)

  return [
    String(store.branch_name ?? ''),
    String(store.business_unit_name ?? ''),
    String(store.branch_code ?? ''),
    String(store.branch_address ?? ''),
    region?.name ?? '',
    province?.province_name ?? '',
    String(store.branch_email ?? ''),
    String(store.contact_person ?? ''),
    String(store.contact_number ?? ''),
    String(store.mobile_number ?? ''),
    String(store.location_address ?? ''),
    String(store.normalized_address ?? ''),
    formatCoordinate(store.latitude),
    formatCoordinate(store.longitude),
    formatStoreActiveForCsv(store.active),
  ]
}

export async function exportStoresToCSV(options: {
  activeTab?: 'all' | 'trash'
  selectedIds?: number[]
  clientCode?: string
  showError?: (error: unknown) => void
  showSuccess?: (title: string, message: string) => void
  showWarning?: (title: string, message?: string) => void
}): Promise<void> {
  const activeTab = options.activeTab ?? 'all'
  const endpoint =
    activeTab === 'trash' ? '/store-management/archived/stores' : '/store-management/stores'
  const timestamp = new Date().toISOString().split('T')[0]
  const clientSuffix = options.clientCode ? `_${options.clientCode}` : ''
  const tabSuffix = activeTab === 'trash' ? '_trash' : ''

  await exportToCSV({
    endpoint,
    selectedIds: options.selectedIds ?? [],
    activeTab,
    csvHeaders: [...STORE_IMPORT_CSV_HEADERS],
    dataMapper: mapStoreToImportCsvRow,
    filename: `stores${clientSuffix}${tabSuffix}_${timestamp}.csv`,
    showError: options.showError,
    showSuccess: options.showSuccess,
    showWarning: options.showWarning,
  })
}
